import {
  Alert,
  Anchor,
  Button,
  Card,
  Group,
  SimpleGrid,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import {
  addDays,
  finalChecksOpen,
  isDone,
  lastVoyageRange,
  type InspectionPlan,
} from '@opti-sea/risq';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { useWorkplace } from '../../../core/access';
import { PageTitle, QueryState, usePositionNames } from '../../../ui/components';
import { formatDate, today } from '../../../ui/format';
import { useFinalChecks, useInspections, useRpc, useVesselItems, useWeekly } from '../data';
import { vesselOverview, weekStart, type VesselItem } from '../model';
import { RecentActivity } from './ActivityPage';
import { LastVoyageLine, PrepLine, ProgressTables } from '../widgets';

/** Planned inspection date and the start of the last voyage (DPA, Admin; Master on the phone). */
function InspectionCard({ vesselId, plan }: { vesselId: string; plan: InspectionPlan | null }) {
  const { t } = useTranslation('dashboard');
  const [date, setDate] = useState(plan?.plannedOn ?? '');
  const [voyage, setVoyage] = useState(plan?.lastVoyageStartedOn ?? '');
  const setInspection = useRpc('risq_set_inspection_date', [['inspections']]);
  const setLastVoyage = useRpc('risq_set_last_voyage', [['inspections']]);
  const overview = plan === null ? null : vesselOverview([], [], plan, today());
  const range = plan === null ? null : lastVoyageRange(today(), plan.plannedOn);
  const error = setInspection.error ?? setLastVoyage.error;

  return (
    <Card withBorder>
      <Stack>
        <Title order={2} size="h3">
          {t('inspection.title')}
        </Title>
        {plan === null || overview?.prep == null ? (
          <Text>{t('inspection.notSetBody')}</Text>
        ) : (
          <Stack gap={2}>
            <Text fw={700}>{t('inspection.planned', { date: formatDate(plan.plannedOn) })}</Text>
            <PrepLine prep={overview.prep} />
            <LastVoyageLine prep={overview.prep} />
          </Stack>
        )}
        <Group align="flex-end">
          <TextInput
            type="date"
            label={t('inspection.dateLabel')}
            value={date}
            onChange={(e) => setDate(e.currentTarget.value)}
          />
          <Button
            loading={setInspection.isPending}
            disabled={date === '' || date === plan?.plannedOn}
            onClick={() => setInspection.mutate({ p_vessel_id: vesselId, p_planned_on: date })}
          >
            {t('inspection.save')}
          </Button>
        </Group>
        {plan === null || range === null ? null : (
          <Group align="flex-end">
            <TextInput
              type="date"
              label={t('inspection.voyageLabel')}
              description={t('inspection.voyageHint')}
              min={range.from}
              max={range.to}
              value={voyage}
              onChange={(e) => setVoyage(e.currentTarget.value)}
            />
            <Button
              variant="default"
              loading={setLastVoyage.isPending}
              disabled={voyage === '' || voyage === plan.lastVoyageStartedOn}
              onClick={() => setLastVoyage.mutate({ p_vessel_id: vesselId, p_started_on: voyage })}
            >
              {t('inspection.voyageSave')}
            </Button>
            {plan.lastVoyageStartedOn == null ? null : (
              <Button
                variant="subtle"
                onClick={() => {
                  setVoyage('');
                  setLastVoyage.mutate({ p_vessel_id: vesselId, p_started_on: null });
                }}
              >
                {t('inspection.voyageClear')}
              </Button>
            )}
          </Group>
        )}
        {error === null ? null : (
          <Alert color="red" role="alert">
            {t('common.saveFailed', { message: error.message })}
          </Alert>
        )}
      </Stack>
    </Card>
  );
}

/** Final checks of the planned inspection (Task 6b): progress per group and the entered values. */
function FinalChecksCard({ vesselId, plan }: { vesselId: string; plan: InspectionPlan }) {
  const { t } = useTranslation('dashboard');
  const { name } = usePositionNames();
  const checks = useFinalChecks(vesselId, plan.plannedOn);
  const overview = checks.data ?? null;
  return (
    <Card withBorder>
      <Stack>
        <Title order={2} size="h3">
          {overview === null
            ? t('finalChecks.title')
            : t('finalChecks.titleProgress', {
                done: overview.summary.done,
                total: overview.summary.total,
                percent: overview.summary.percent,
              })}
        </Title>
        <QueryState loading={checks.isPending} error={checks.error}>
          {() =>
            overview === null ? (
              <Text>{t('finalChecks.noList')}</Text>
            ) : (
              <>
                <Text>
                  {finalChecksOpen(today(), plan)
                    ? t('finalChecks.open', { date: formatDate(plan.plannedOn) })
                    : t('finalChecks.opensOn', {
                        date: formatDate(addDays(plan.plannedOn, -1)),
                        inspection: formatDate(plan.plannedOn),
                      })}
                </Text>
                <Table>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>{t('finalChecks.group')}</Table.Th>
                      <Table.Th>{t('finalChecks.tickedBy')}</Table.Th>
                      <Table.Th>{t('finalChecks.done')}</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {overview.groups.map((g) => (
                      <Table.Tr key={g.id}>
                        <Table.Td>{g.titleEn}</Table.Td>
                        <Table.Td>{g.positionCodes.map(name).join(', ')}</Table.Td>
                        <Table.Td>
                          {t('vessel.weekDone', { done: g.summary.done, total: g.summary.total })}
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
                {overview.values.map((v) => (
                  <Text key={v.itemId}>
                    {v.value === null
                      ? t('finalChecks.valueEmpty', { label: v.labelEn })
                      : t('finalChecks.valueSet', {
                          label: v.labelEn,
                          value: v.kind === 'date' ? formatDate(v.value) : v.value,
                          position: v.changedPosition === null ? '' : name(v.changedPosition),
                        })}
                  </Text>
                ))}
              </>
            )
          }
        </QueryState>
      </Stack>
    </Card>
  );
}

function WeekCard({ vesselId, items }: { vesselId: string; items: VesselItem[] }) {
  const { t } = useTranslation('dashboard');
  const { name } = usePositionNames();
  const week = weekStart(new Date());
  const weekly = useWeekly(vesselId, [week]);
  const byId = new Map(items.map((i) => [i.id, i]));
  const byPosition = new Map<string, VesselItem[]>();
  for (const row of weekly.data ?? []) {
    const item = byId.get(row.item_id);
    if (item) {
      byPosition.set(row.position_code, [...(byPosition.get(row.position_code) ?? []), item]);
    }
  }
  return (
    <Card withBorder>
      <Stack>
        <Group justify="space-between">
          <Title order={2} size="h3">
            {t('vessel.week', { date: formatDate(week) })}
          </Title>
          <Anchor component={Link} to={`/weekly?vessel=${vesselId}`}>
            {t('vessel.chooseWeek')}
          </Anchor>
        </Group>
        {byPosition.size === 0 ? (
          <Text>{t('vessel.weekEmpty')}</Text>
        ) : (
          <Table>
            <Table.Tbody>
              {[...byPosition].map(([code, list]) => (
                <Table.Tr key={code}>
                  <Table.Td>{name(code)}</Table.Td>
                  <Table.Td>
                    {t('vessel.weekDone', {
                      done: list.filter((i) => isDone(i.status)).length,
                      total: list.length,
                    })}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Stack>
    </Card>
  );
}

/** D3 Vessel: inspection planning, progress per position and flag, this week, recent activity. */
export function VesselPage() {
  const { t } = useTranslation('dashboard');
  const { vesselId = '' } = useParams();
  const { vessels, access } = useWorkplace();
  const vessel = vessels.find((v) => v.id === vesselId);
  const { items, loading, error } = useVesselItems(vesselId);
  const inspections = useInspections();
  if (vessel === undefined) {
    return <Text>{t('vessel.notFound')}</Text>;
  }
  const plan = inspections.data?.get(vessel.id) ?? null;
  return (
    <>
      <PageTitle title={vessel.name} back={{ to: '/', label: t('nav.fleet') }}>
        <Group>
          <Button component={Link} to={`/vessels/${vessel.id}/questions`}>
            {t('vessel.questions')}
          </Button>
          <Button component={Link} to={`/vessels/${vessel.id}/crew`} variant="default">
            {t('vessel.crew')}
          </Button>
        </Group>
      </PageTitle>
      <Text c="dimmed" mb="md">
        {[t('fleet.imo', { imo: vessel.imo }), vessel.vesselType, vessel.flag]
          .filter((part) => part !== null && part !== '')
          .join(' · ')}
      </Text>
      <QueryState loading={loading || inspections.isPending} error={error ?? inspections.error}>
        {() => {
          const list = items ?? [];
          const overview = vesselOverview(
            list,
            access.positions.map((p) => p.code),
            plan,
            today(),
          );
          const waiting = list.filter((i) => i.status === 'office_action');
          return (
            <Stack gap="lg">
              <SimpleGrid cols={{ base: 1, md: 2 }}>
                <InspectionCard key={plan?.plannedOn ?? 'none'} vesselId={vessel.id} plan={plan} />
                <WeekCard vesselId={vessel.id} items={list} />
              </SimpleGrid>
              <Card withBorder>
                <Stack>
                  <Title order={2} size="h3">
                    {t('vessel.progress', {
                      done: overview.summary.done,
                      total: overview.summary.total,
                      percent: overview.summary.percentDone,
                    })}
                  </Title>
                  <ProgressTables overview={overview} />
                </Stack>
              </Card>
              {plan === null ? null : <FinalChecksCard vesselId={vessel.id} plan={plan} />}
              <Card withBorder>
                <Stack>
                  <Title order={2} size="h3">
                    {t('waiting.titleCount', { count: waiting.length })}
                  </Title>
                  {waiting.length === 0 ? <Text>{t('waiting.empty')}</Text> : null}
                  {waiting.slice(0, 10).map((i) => (
                    <Anchor
                      key={i.id}
                      component={Link}
                      to={`/vessels/${vessel.id}/questions/${i.id}`}
                      lineClamp={2}
                    >
                      {t('question.codeText', { code: i.question.code, text: i.question.textEn })}
                    </Anchor>
                  ))}
                  {waiting.length > 10 ? (
                    <Anchor component={Link} to={`/waiting?vessel=${vessel.id}`} fw={600}>
                      {t('waiting.all', { count: waiting.length })}
                    </Anchor>
                  ) : null}
                </Stack>
              </Card>
              <Card withBorder>
                <Stack>
                  <Group justify="space-between">
                    <Title order={2} size="h3">
                      {t('activity.recent')}
                    </Title>
                    <Anchor component={Link} to={`/vessels/${vessel.id}/activity`}>
                      {t('activity.all')}
                    </Anchor>
                  </Group>
                  <RecentActivity vesselId={vessel.id} items={list} limit={10} />
                </Stack>
              </Card>
            </Stack>
          );
        }}
      </QueryState>
    </>
  );
}
